import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import ProductsPage from "@/pages/ProductsPage";
import ExpirationsPage from "@/pages/ExpirationsPage";
import WastePage from "@/pages/WastePage";
import TemperaturesPage from "@/pages/TemperaturesPage";
import HaccpPage from "@/pages/HaccpPage";
import NonConformitiesPage from "@/pages/NonConformitiesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import KitchenModePage from "@/pages/KitchenModePage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import { Loader2 } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );
}

function AuthCallback() {
  const { setData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.replace("#", "").split("=").pop();
    api.post("/auth/google/session", { session_id: sessionId })
      .then(() => api.get("/auth/me"))
      .then((me) => {
        setData(me.data);
        const locs = me.data.locations || [];
        if (locs.length) localStorage.setItem("sk_loc", locs[0].location_id);
        navigate(me.data.user.org_id ? "/dashboard" : "/onboarding", { replace: true });
      })
      .catch(() => navigate("/login", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LoadingScreen />;
}

function Protected({ children, requireOrg = true }) {
  const { data } = useAuth();
  if (data === null) return <LoadingScreen />;
  if (data === false) return <Navigate to="/login" replace />;
  if (requireOrg && !data.user?.org_id) return <Navigate to="/onboarding" replace />;
  if (!requireOrg && data.user?.org_id) return <Navigate to="/dashboard" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { data } = useAuth();
  if (data === null) return <LoadingScreen />;
  if (data !== false) return <Navigate to={data.user?.org_id ? "/dashboard" : "/onboarding"} replace />;
  return children;
}

function LandingGate() {
  const { data } = useAuth();
  if (data === null) return <LoadingScreen />;
  if (data !== false) return <Navigate to={data.user?.org_id ? "/dashboard" : "/onboarding"} replace />;
  return <LandingPage />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
      <Route path="/onboarding" element={<Protected requireOrg={false}><OnboardingPage /></Protected>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="magazzino" element={<ProductsPage />} />
        <Route path="scadenze" element={<ExpirationsPage />} />
        <Route path="sprechi" element={<WastePage />} />
        <Route path="temperature" element={<TemperaturesPage />} />
        <Route path="haccp" element={<HaccpPage />} />
        <Route path="non-conformita" element={<NonConformitiesPage />} />
        <Route path="notifiche" element={<NotificationsPage />} />
        <Route path="kitchen" element={<KitchenModePage />} />
        <Route path="prezzi" element={<PricingPage />} />
        <Route path="payment/success" element={<PaymentSuccessPage />} />
        <Route path="payment/cancel" element={<PaymentCancelPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
